import {
  ApiBlueprint,
  createFrontendPlugin,
  createRouteRef,
  discoveryApiRef,
  fetchApiRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { PrivacyClient, privacyApiRef } from './api';
import { AccessibilityClient, accessibilityApiRef } from './accessibility-api';
import { AgenticClient, agenticApiRef, oidcAuthApiRef } from './agentic-api';
import { agenticRouteRef } from './routes';

const privacyApi = ApiBlueprint.make({
  name: 'privacy',
  params: defineParams =>
    defineParams({
      api: privacyApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: deps => new PrivacyClient(deps),
    }),
});

const accessibilityApi = ApiBlueprint.make({
  name: 'accessibility',
  params: defineParams =>
    defineParams({
      api: accessibilityApiRef,
      deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
      factory: deps => new AccessibilityClient(deps),
    }),
});

const agenticApi = ApiBlueprint.make({
  name: 'agentic',
  params: defineParams =>
    defineParams({
      api: agenticApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
        oidcAuthApi: oidcAuthApiRef,
      },
      factory: deps => new AgenticClient(deps),
    }),
});

export const anonymizationRouteRef = createRouteRef();
export const accessibilityRouteRef = createRouteRef();

/** Document anonymization, reachable from the main navigation. */
const anonymizationPage = PageBlueprint.make({
  name: 'anonymisierung',
  params: {
    path: '/anonymisierung',
    // The main navigation lists pages that have a route ref, a title and an icon.
    routeRef: anonymizationRouteRef,
    title: 'Dokumente anonymisieren',
    icon: (
      <span
        className="kern-icon kern-icon--visibility-off"
        aria-hidden="true"
      />
    ),
    loader: () =>
      import('./components/AnonymizationPage').then(m => (
        <m.AnonymizationPage />
      )),
  },
});

/** Accessibility check of meeting rooms, with sub pages for upload and review. */
const accessibilityPage = PageBlueprint.make({
  name: 'barrierefreiheit',
  params: {
    path: '/barrierefreiheit',
    routeRef: accessibilityRouteRef,
    title: 'Barrierefreiheit prüfen',
    icon: (
      <span className="kern-icon kern-icon--checklist" aria-hidden="true" />
    ),
    loader: () =>
      import('./components/accessibility/AccessibilityPage').then(m => (
        <m.AccessibilityPage />
      )),
  },
});

/** TC agentic demonstrator: the same kind of check, run by n8n AI agents. */
const agenticPage = PageBlueprint.make({
  name: 'konferenzraum-pruefung',
  params: {
    path: '/konferenzraum-pruefung',
    routeRef: agenticRouteRef,
    title: 'Konferenzraum-Prüfung (Agentic)',
    icon: (
      <span className="kern-icon kern-icon--autorenew" aria-hidden="true" />
    ),
    loader: () =>
      import('./components/agentic/AgenticPage').then(m => <m.AgenticPage />),
  },
});

/**
 * VisionFlow business pages in the KERN look: document anonymization and the
 * accessibility check of meeting rooms.
 */
export const visionflowPlugin = createFrontendPlugin({
  pluginId: 'visionflow',
  extensions: [
    privacyApi,
    accessibilityApi,
    agenticApi,
    anonymizationPage,
    accessibilityPage,
    agenticPage,
  ],
  routes: {
    anonymization: anonymizationRouteRef,
    accessibility: accessibilityRouteRef,
    agentic: agenticRouteRef,
  },
});
